import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useSearchParams } from "react-router-dom";

import {
  Input,
  NormalText,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../common/native";
import StripCheckoutForm from "../home/upgrade/StripeCheckoutForm";
import { Stars } from "../../../assets";
import { TPlan } from "../../../utils/types";
import { mixpanel } from "../../../utils/mixpanel";
import {
  getBillingSummaryWithApiKey,
  updatePlanWithApiKey,
} from "../../../services/api-billing";
import { TPlanGetResponse } from "../../../services/api";

const PLAN_ORDER: TPlan[] = ["FREE", "ENTRY", "CORE", "PREMIUM"];
const SHOW_PREMIUM_PLAN = false; // Intentionally hidden for now. Keep Premium logic in place for a future release.

type PlanAction = "current" | "upgrade" | "downgrade" | "contact";

const planRank = (plan: TPlan) => PLAN_ORDER.indexOf(plan);

const determinePlanAction = (
  target: TPlan,
  current?: TPlan | null,
): PlanAction => {
  if (!current) return "upgrade";
  if (target === "PREMIUM") {
    return current === "PREMIUM" ? "current" : "contact";
  }
  if (target === current) return "current";
  return planRank(target) > planRank(current) ? "upgrade" : "downgrade";
};

const planActionLabel = (action: PlanAction) => {
  if (action === "current") return "Current Plan";
  if (action === "upgrade") return "Upgrade";
  if (action === "downgrade") return "Downgrade";
  if (action === "contact") return "Contact Us";
  return "Select";
};

const formatPlanName = (plan: TPlan) => {
  switch (plan) {
    case "FREE":
      return "Free";
    case "ENTRY":
      return "Entry";
    case "CORE":
      return "Core";
    case "PREMIUM":
      return "Premium";
    default:
      return plan;
  }
};

const PublicBillingPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [apiKeyInput, setApiKeyInput] = useState(
    () => searchParams.get("api_key")?.trim() ?? "",
  );
  const [clinicUuidInput, setClinicUuidInput] = useState(
    () => searchParams.get("clinic_uuid")?.trim() ?? "",
  );

  const [apiKey, setApiKey] = useState(
    () => searchParams.get("api_key")?.trim() ?? "",
  );
  const [clinicUuid, setClinicUuid] = useState(
    () => searchParams.get("clinic_uuid")?.trim() ?? "",
  );

  const [billingSummary, setBillingSummary] = useState<TPlanGetResponse | null>(
    null,
  );
  const [billingError, setBillingError] = useState<string | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState<boolean>(false);

  const [planStatus, setPlanStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [planActionLoading, setPlanActionLoading] = useState<TPlan | null>(
    null,
  );
  const [checkoutPlan, setCheckoutPlan] = useState<TPlan | null>(null);
  const [showCredentialsCard, setShowCredentialsCard] = useState<boolean>(
    !(apiKey && clinicUuid),
  );

  const loadBillingData = useCallback(
    async (currentApiKey: string, currentClinicUuid: string) => {
      setBillingError(null);
      setIsLoadingBilling(true);
      try {
        const summary = await getBillingSummaryWithApiKey(
          currentApiKey,
          currentClinicUuid,
        );
        setBillingSummary(summary);
        setPlanStatus(null);
        setShowCredentialsCard(false);
      } catch (error) {
        console.error(error);
        setBillingSummary(null);
        setBillingError(
          error instanceof Error
            ? error.message
            : "Failed to load billing data. Check your API key and clinic identifier.",
        );
      } finally {
        setIsLoadingBilling(false);
      }
    },
    [setShowCredentialsCard],
  );

  useEffect(() => {
    if (!apiKey || !clinicUuid) {
      setBillingSummary(null);
      return;
    }
    loadBillingData(apiKey, clinicUuid);
  }, [apiKey, clinicUuid, loadBillingData]);

  const handleConnect = () => {
    const trimmedApiKey = apiKeyInput.trim();
    const trimmedClinicUuid = clinicUuidInput.trim();

    if (!trimmedApiKey || !trimmedClinicUuid) {
      return;
    }

    setPlanStatus(null);
    setCheckoutPlan(null);
    setBillingError(null);
    setShowCredentialsCard(false);

    if (trimmedApiKey === apiKey && trimmedClinicUuid === clinicUuid) {
      loadBillingData(trimmedApiKey, trimmedClinicUuid);
      return;
    }

    setApiKey(trimmedApiKey);
    setClinicUuid(trimmedClinicUuid);
  };

  const handleDisconnect = () => {
    setApiKey("");
    setClinicUuid("");
    setApiKeyInput("");
    setClinicUuidInput("");
    setShowCredentialsCard(true);
    setBillingSummary(null);
    setPlanStatus(null);
    setCheckoutPlan(null);
    setBillingError(null);
  };

  const handlePlanSelection = async (targetPlan: TPlan) => {
    if (!apiKey || !clinicUuid || !billingSummary) return;

    const action = determinePlanAction(targetPlan, billingSummary.plan_type);

    if (action === "current") return;

    if (targetPlan === "PREMIUM") {
      if (action === "contact" && typeof window !== "undefined") {
        mixpanel.track("Upgrade Contact Us Clicked", {
          Source: "PublicBillingPage",
          Package: "PREMIUM",
          "Clinic Id": clinicUuid,
        });
        window.open(
          "https://meetings.hubspot.com/kav-ohiomoba?uuid=000a6c6c-4e10-4fc7-876d-7907cc133fb6",
          "_blank",
        );
      }
      return;
    }

    if (action === "upgrade" && billingSummary.plan_type === "FREE") {
      setCheckoutPlan(targetPlan);
      setPlanStatus(null);
      return;
    }

    if (
      action === "downgrade" &&
      typeof window !== "undefined" &&
      !window.confirm(
        `Downgrade to the ${formatPlanName(targetPlan)} plan? This change takes effect immediately.`,
      )
    ) {
      return;
    }

    setPlanActionLoading(targetPlan);
    setPlanStatus(null);

    try {
      await updatePlanWithApiKey(apiKey, clinicUuid, targetPlan);
      setPlanStatus({
        type: "success",
        message: `Plan updated to ${formatPlanName(targetPlan)}.`,
      });
      await loadBillingData(apiKey, clinicUuid);
    } catch (error) {
      console.error(error);
      setPlanStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "We could not update that plan. Please try again.",
      });
    } finally {
      setPlanActionLoading(null);
    }
  };

  const currentPlan = billingSummary?.plan_type ?? null;

  const entryAction = determinePlanAction("ENTRY", currentPlan);
  const coreAction = determinePlanAction("CORE", currentPlan);
  const premiumAction = determinePlanAction("PREMIUM", currentPlan);

  const entryButtonText = planActionLabel(entryAction);
  const coreButtonText = planActionLabel(coreAction);
  const premiumButtonText = planActionLabel(premiumAction);

  if (checkoutPlan && apiKey && clinicUuid) {
    return (
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.checkoutContainer}
        showsVerticalScrollIndicator={false}
      >
        <TitleText text="Finish your upgrade" />
        <NormalText
          text={`Complete checkout to move to the ${formatPlanName(checkoutPlan)} plan.`}
          style={styles.checkoutLead}
        />
        <View style={styles.checkoutCard}>
          <SecondaryButton
            text="Back to plans"
            onPress={() => setCheckoutPlan(null)}
            fullWidth
          />
          <StripCheckoutForm
            plan={checkoutPlan}
            apiKey={apiKey}
            clinicUuid={clinicUuid}
          />
          <NormalText
            text="When checkout finishes you can refresh this page to see the updated plan."
            style={styles.checkoutHint}
          />
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.navBarWrapper}>
        <View style={styles.navBar}>
          <Image
            source={{
              uri: "https://clinic-os.com/user-app-assets/header-clinicos.png",
            }}
            style={styles.navLogo}
          />
        </View>
      </View>

      {showCredentialsCard ? (
        <View style={styles.card}>
          <TitleText text="API key authentication" style={styles.cardTitle} />
          <Input
            name="API key"
            placeholder="prefix.secret"
            value={apiKeyInput}
            onChangeText={setApiKeyInput}
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
          />
          <Input
            name="Clinic UUID"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={clinicUuidInput}
            onChangeText={setClinicUuidInput}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <NormalText
            text="Keys and clinic identifiers stay in memory only. Reloading without the query parameters clears them."
            style={styles.authHint}
          />
          <View style={styles.credentialsActions}>
            <PrimaryButton
              text="Connect"
              onPress={handleConnect}
              disabled={!apiKeyInput.trim() || !clinicUuidInput.trim()}
            />
            <SecondaryButton
              text="Clear"
              onPress={handleDisconnect}
              disabled={!apiKey && !clinicUuid}
            />
          </View>
        </View>
      ) : null}

      {/* Connection summary hidden during early rollout */}

      {billingError ? (
        <View style={styles.errorBanner}>
          <NormalText text={billingError} style={styles.errorBannerText} />
        </View>
      ) : null}

      {isLoadingBilling ? (
        <View style={styles.loader}>
          <ActivityIndicator size="small" color="#00B7F4" />
        </View>
      ) : null}

      {billingSummary ? (
        <View style={styles.summaryCard}>
          <NormalText
            text={`Current plan: ${formatPlanName(billingSummary.plan_type)}`}
            style={styles.summaryPrimary}
          />
          <NormalText
            text={`Remaining free simulations: ${billingSummary.remaining_free_simulation_count}`}
            style={styles.summarySecondary}
          />
          {billingSummary.is_payment_delinquent ? (
            <View style={styles.delinquentBanner}>
              <NormalText
                text="Payment delinquent. Update your card to restore service."
                style={styles.delinquentText}
              />
            </View>
          ) : null}
        </View>
      ) : null}

      {planStatus ? (
        <View
          style={[
            styles.statusBanner,
            planStatus.type === "success"
              ? styles.statusSuccess
              : styles.statusError,
          ]}
        >
          <NormalText
            text={planStatus.message}
            style={
              planStatus.type === "success"
                ? styles.statusSuccessText
                : styles.statusErrorText
            }
          />
        </View>
      ) : null}

      {billingSummary ? (
        <View style={styles.cardsWrapper}>
          <View style={styles.planCard}>
            <View style={styles.planCardContent}>
              <TitleText text="Entry" fontWeight="700" />
              <NormalText text="$99 / month" style={styles.planPrice} />
              <PlanFeatures
                items={[
                  "Unlimited AI simulations",
                  "Unlimited voice notes",
                  "Unlimited before/afters",
                  "All procedures",
                  "iPad & iPhone app",
                  "Email support",
                ]}
              />
            </View>
            <SecondaryButton
              text={entryButtonText}
              onPress={() => handlePlanSelection("ENTRY")}
              loading={planActionLoading === "ENTRY"}
              disabled={entryAction === "current"}
              roundBorderRadius
              style={styles.planCardButton}
            />
          </View>

          <View style={styles.planCardFeatured}>
            <View style={styles.mostPopular}>
              <TitleText
                text="Most Popular"
                style={styles.mostPopularText}
                fontWeight="700"
              />
            </View>
            <View style={[styles.planCard, styles.planCardElevated]}>
              <View style={styles.planCardContent}>
                <TitleText text="Core" fontWeight="700" />
                <Text style={styles.corePrice}>
                  <Text style={styles.corePriceStrikethrough}>$279</Text>{" "}
                  <Text style={styles.corePriceHighlight}>$199</Text> / month
                </Text>
                <PlanFeatures
                  items={[
                    "Everything in Entry",
                    "Full EMR integration",
                    "Photo management",
                    "Semantic search",
                    "Priority support",
                  ]}
                />
              </View>
              <PrimaryButton
                text={coreButtonText}
                onPress={() => handlePlanSelection("CORE")}
                loading={planActionLoading === "CORE"}
                disabled={coreAction === "current"}
                roundBorderRadius
                style={styles.planCardButton}
              />
            </View>
          </View>

          {SHOW_PREMIUM_PLAN ? (
            <View style={styles.planCard}>
              <View style={styles.planCardContent}>
                <TitleText text="Premium" fontWeight="700" />
                <View style={styles.premiumHeader}>
                  <Stars />
                  <NormalText text="Custom" style={styles.planPrice} />
                </View>
                <PlanFeatures
                  items={[
                    "Everything in Core, plus...",
                    "Custom AI tools for your practice",
                  ]}
                />
              </View>
              <SecondaryButton
                text={premiumButtonText}
                onPress={() => handlePlanSelection("PREMIUM")}
                disabled={premiumAction === "current"}
                roundBorderRadius
                style={styles.planCardButton}
              />
            </View>
          ) : null}

          <View style={styles.planCard}>
            <View style={styles.planCardContent}>
              <TitleText text="Enterprise" fontWeight="700" />
              <NormalText text="Custom pricing" style={styles.planPrice} />
              <PlanFeatures
                items={[
                  "Everything in Core",
                  "Multi-location support",
                  "API access",
                  "Custom integrations",
                  "Dedicated account manager",
                  "Custom onboarding",
                ]}
              />
            </View>
            <SecondaryButton
              text="Contact Us"
              onPress={() => {
                mixpanel.track("Upgrade Contact Us Clicked", {
                  Source: "PublicBillingPage",
                  Package: "ENTERPRISE",
                  "Clinic Id": clinicUuid,
                });
                if (typeof window !== "undefined") {
                  window.open("https://clinicos.ai/contact", "_blank");
                }
              }}
              roundBorderRadius
              style={styles.planCardButton}
            />
          </View>
        </View>
      ) : null}
    </ScrollView>
  );
};

type PlanFeaturesProps = {
  items: string[];
};

const PlanFeatures: React.FC<PlanFeaturesProps> = ({ items }) => (
  <View style={styles.featureList}>
    {items.map((item) => (
      <NormalText key={item} text={item} style={styles.featureItem} />
    ))}
  </View>
);

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  content: {
    gap: 24,
    paddingBottom: 80,
  },
  navBarWrapper: {
    width: "100%",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E4EA",
    marginBottom: 24,
  },
  navBar: {
    width: "100%",
    maxWidth: 1280,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  navLogo: {
    width: 116,
    height: 52,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    padding: 20,
    gap: 16,
  },
  cardTitle: {
    fontSize: 20,
  },
  authHint: {
    fontSize: 14,
    color: "#636776",
  },
  credentialsActions: {
    flexDirection: "row",
    gap: 12,
  },
  summaryCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    backgroundColor: "#FFFFFF",
    padding: 20,
    gap: 8,
  },
  summaryPrimary: {
    fontFamily: "Urbanist600",
    color: "#1F242F",
  },
  summarySecondary: {
    color: "#4D5360",
    fontSize: 14,
  },
  delinquentBanner: {
    marginTop: 4,
    padding: 12,
    borderRadius: 12,
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
  },
  delinquentText: {
    color: "#981B1B",
  },
  errorBanner: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#FECACA",
    backgroundColor: "#FEF2F2",
    padding: 12,
  },
  errorBannerText: {
    color: "#981B1B",
  },
  loader: {
    paddingVertical: 12,
  },
  statusBanner: {
    borderRadius: 12,
    padding: 12,
  },
  statusSuccess: {
    backgroundColor: "#EBF9EF",
    borderColor: "#A7F3D0",
    borderWidth: 1,
  },
  statusError: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
    borderWidth: 1,
  },
  statusSuccessText: {
    color: "#065F46",
  },
  statusErrorText: {
    color: "#981B1B",
  },
  cardsWrapper: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    justifyContent: "center",
  },
  planCard: {
    width: "100%",
    maxWidth: 347,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    backgroundColor: "#FFFFFF",
    padding: 24,
    gap: 16,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 480,
    position: "relative",
  },
  planCardFeatured: {
    width: "100%",
    maxWidth: 347,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "transparent",
    borderRadius: 16,
    alignItems: "stretch",
  },
  planCardElevated: {
    marginTop: -16,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderWidth: 1,
    borderTopWidth: 0,
  },
  planCardContent: {
    gap: 16,
    flexGrow: 1,
  },
  planCardButton: {
    marginTop: "auto",
  },
  mostPopular: {
    backgroundColor: "#00B7F4",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: "center",
  },
  mostPopularText: {
    color: "#FFFFFF",
  },
  planPrice: {
    marginBottom: 12,
  },
  corePrice: {
    fontFamily: "Urbanist500",
    color: "#000000",
    fontSize: 16,
    marginBottom: 12,
  },
  corePriceStrikethrough: {
    textDecorationLine: "line-through",
  },
  corePriceHighlight: {
    color: "#00B7F4",
  },
  featureList: {
    gap: 12,
  },
  featureItem: {
    color: "#4D5360",
  },
  premiumHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkoutContainer: {
    gap: 24,
    paddingBottom: 80,
  },
  checkoutLead: {
    color: "#4D5360",
    maxWidth: 640,
  },
  checkoutCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    padding: 20,
    gap: 16,
  },
  checkoutHint: {
    color: "#4D5360",
    fontSize: 14,
  },
});

export default PublicBillingPage;
